// src/router/index.ts
import Vue from 'vue'
import VueRouter, { RouteConfig } from 'vue-router'
import store from '../store'

import Home from '../views/Patients/Patients.vue'
import InsurancesView from '../views/Insurances.vue'
import DiagnosticsView from '../views/Diagnostics.vue'
import RecordsView from '../views/Records.vue'
import CalculatorView from '../views/Calculator.vue'
import Gallery from '../views/Gallery/Gallery.vue'
import GalleryFile from '../views/Gallery/GalleryFile.vue'
import LoginReport from '../views/Admin/Users/LoginReport.vue'
import RafScoreHistory from '../views/Admin/RafScoreHistory.vue'
import PatientList from '../views/Patients/List.vue'
import DoctorsView from '../views/Doctors/List.vue'
import UsersView from '../views/Admin/Users/List.vue'
import NotificationsView from '../views/Admin/Users/Notifications.vue'
import InsuranceGeneralReport from '@/components/reports/insurance-general-report.vue'
import InsuranceBFS from '@/components/reports/insurance-bfs-report.vue'
import InsuranceBSS from '@/components/reports/insurance-bss-report.vue'
import CompleteProfile from '@/views/Auth/CompleteProfile.vue'
import LoginSummary from '@/components/reports/loginSummary.vue'
import DeletedDxReport from '@/components/reports/deleted-dx-report.vue'

import {
  isAuthenticated,
  needsADoctor
} from '@/components/admin/users/users.service'
import { CurrentUser } from '@/models'

Vue.use(VueRouter)

/**
 * Permission helpers (reads currentUser.menus from localStorage)
 * Each menu item (and nested menus) includes options[] with accessName
 */
type MenuOption = { accessName?: string }
type MenuNode = { menus?: MenuNode[] | null; options?: MenuOption[] | null }

function getUserPermissions(): Set<string> {
  const raw = localStorage.getItem('currentUser')
  if (!raw) return new Set<string>()

  try {
    const user = JSON.parse(raw)
    const menus = (user?.menus || []) as MenuNode[]

    const perms = new Set<string>()
    const walk = (node: MenuNode) => {
      node.options?.forEach(o => o.accessName && perms.add(o.accessName))
      node.menus?.forEach(walk)
    }
    menus.forEach(walk)
    return perms
  } catch {
    return new Set<string>()
  }
}

function hasPermission(required?: string): boolean {
  if (!required) return false
  return getUserPermissions().has(required)
}

const routes: Array<RouteConfig> = [
  // ---------- Public routes ----------
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/Auth/Login.vue'),
    meta: { needsAuth: false, allowAll: true }
  },
  {
    path: '/OTP',
    name: 'OtpLoginPage',
    component: () => import('@/views/Auth/OtpLoginPage.vue'),
    meta: { needsAuth: false, allowAll: true }
  },
  {
    path: '/changePassword',
    name: 'ChangePassword',
    component: () => import('@/views/Auth/ChangePassword.vue'),
    meta: { needsAuth: false, allowAll: true }
  },
  {
    path: '/signUp',
    name: 'SignUp',
    component: () => import('@/views/Auth/SignUp.vue'),
    meta: { needsAuth: false, allowAll: true },
    props: true
  },
  {
    path: '/underMaintenance',
    name: 'UnderMaintenance',
    component: () =>
      import(
        /* webpackChunkName: "underMaintenance" */ '../views/UnderMaintenance.vue'
      ),
    meta: { needsAuth: false, allowAll: true }
  },
  {
    path: '/privacy',
    name: 'PrivacyPolicy2',
    component: () =>
      import(/* webpackChunkName: "about" */ '../views/PrivacyPolicy.vue'),
    meta: { needsAuth: false, allowAll: true }
  },
  {
    path: '/termsandconditions',
    name: 'Terms2',
    component: () =>
      import(/* webpackChunkName: "about" */ '../views/Terms.vue'),
    meta: { needsAuth: false, allowAll: true }
  },

  // ---------- Authenticated common routes ----------
  {
    path: '/unauthorized',
    name: 'Unauthorized',
    component: () => import('@/views/Auth/Unauthorized.vue'),
    meta: { needsAuth: true, allowAll: true }
  },
  {
    path: '/doctorSelection',
    name: 'DoctorSelection',
    component: () => import('@/views/Auth/DoctorSelection.vue'),
    meta: { needsAuth: true, allowAll: true }
  },

  // ---------- Layout + secured app ----------
  {
    path: '/',
    redirect: '/dashboard',
    component: () => import('@/views/Layout/Layout.vue'),
    children: [
      // Global (logged-in) screens
      {
        path: '/dashboard',
        name: 'Dashboard',
        component: () =>
          import(/* webpackChunkName: "about" */ '../views/Dashboard.vue'),
        meta: { needsAuth: true, allowAll: true }
      },
      {
        path: '/completeProfile',
        name: 'CompleteProfile',
        component: CompleteProfile,
        meta: { needsAuth: true, allowAll: true }
      },
      {
        path: '/about',
        name: 'About',
        component: () =>
          import(/* webpackChunkName: "about" */ '../views/About.vue'),
        meta: { needsAuth: true, allowAll: true }
      },
      {
        path: '/privacyPolicy',
        name: 'PrivacyPolicy',
        component: () =>
          import(/* webpackChunkName: "about" */ '../views/PrivacyPolicy.vue'),
        meta: { needsAuth: true, allowAll: true }
      },
      {
        path: '/terms',
        name: 'Terms',
        component: () =>
          import(/* webpackChunkName: "about" */ '../views/Terms.vue'),
        meta: { needsAuth: true, allowAll: true }
      },
      {
        path: '/support',
        name: 'Support',
        component: () =>
          import(
            /* webpackChunkName: "support" */ '../views/Support/index.vue'
          ),
        meta: { needsAuth: true, allowAll: true } // or permission: 'ViewSupport'
      },

      // Permission-protected screens (match backend AccessName)
      {
        path: '/patients',
        name: 'PatientsView',
        component: Home,
        meta: { needsAuth: true, permission: 'ViewPatients' }
      },
      {
        path: '/patientList',
        name: 'PatientsListView',
        component: PatientList,
        meta: { needsAuth: true, permission: 'ViewPatientList' }
      },
      {
        path: '/magCalculator',
        name: 'CalculatorView',
        component: CalculatorView,
        meta: { needsAuth: true, permission: 'ViewRafCalculator' }
      },
      {
        path: '/doctors',
        name: 'DoctorsView',
        component: DoctorsView,
        meta: { needsAuth: true, permission: 'ViewDoctors' } // add in backend if needed
      },
      {
        path: '/admin/users',
        name: 'UsersView',
        component: UsersView,
        meta: { needsAuth: true, permission: 'ViewAdminUsers' }
      },
      {
        path: '/admin/notifications',
        name: 'Notifications',
        component: NotificationsView,
        meta: { needsAuth: true, permission: 'ViewGallery' }
      },
      {
        path: '/admin/loginReport',
        name: 'LoginReport',
        component: LoginReport,
        meta: { needsAuth: true, permission: 'ViewAdminLoginReport' }
      },
      {
        path: '/admin/rafScoreHistory',
        name: 'RafScoreHistory',
        component: RafScoreHistory,
        meta: { needsAuth: true, permission: 'ViewAdminRafScoreHistory' }
      },
      {
        path: '/admin/forms',
        name: 'FormsManagement',
        component: () =>
          import(/* webpackChunkName: "about" */ '../views/Admin/forms.vue'),
        meta: { needsAuth: true, permission: 'ViewAdminForms' }
      },
      // {
      //   path: '/upload',
      //   name: 'Upload',
      //   component: () =>
      //     import(/* webpackChunkName: "about" */ '../views/Upload.vue'),
      //   meta: { needsAuth: true, permission: 'ViewUpload' }
      // },
      {
        path: '/upload',
        name: 'UploadMassivePatientView',
        component: () =>
          import(
            /* webpackChunkName: "about" */ '../views/UploadMassivePatients.vue'
          ),
        meta: { needsAuth: true, permission: 'ViewUpload' }
      },
      {
        path: '/insurances',
        name: 'Insurances',
        component: InsurancesView,
        meta: { needsAuth: true, permission: 'ViewInsurances' }
      },
      {
        path: '/insuranceAgreements',
        name: 'InsuranceAgreements',
        component: () =>
          import(
            /* webpackChunkName: "about" */ '../views/InsuranceAgreements.vue'
          ),
        meta: { needsAuth: true, permission: 'ViewInsuranceAgreements' }
      },
      {
        path: '/diagnostics',
        name: 'Diagnostics',
        component: DiagnosticsView,
        meta: { needsAuth: true, permission: 'ViewDiagnostics' }
      },
      {
        path: '/gallery',
        name: 'Gallery',
        component: Gallery,
        meta: { needsAuth: true, permission: 'ViewGallery' }
      },
      {
        path: '/galleryFile/:id',
        name: 'GalleryFile',
        props: true,
        component: GalleryFile,
        meta: { needsAuth: true, requiresAny: ['ViewGallery'] }
      },

      // Records (add proper backend permission)
      {
        path: '/record/:recordID',
        props: true,
        name: 'RecordsView',
        component: RecordsView,
        meta: { needsAuth: true, requiresAny: ['ViewPatients'] } // add in backend
      },

      // Reports
      {
        path: '/reports/loginSummary',
        name: 'LoginSummary',
        component: LoginSummary,
        meta: { needsAuth: true, permission: 'ViewLoginSummaryReport' }
      },
      {
        path: '/reports/insuranceGeneral',
        name: 'InsuranceGeneralReport',
        component: InsuranceGeneralReport,
        meta: { needsAuth: true, permission: 'ViewInsuranceGenReport' }
      },
      {
        path: '/reports/insuranceBFS',
        name: 'InsuranceBFS',
        component: InsuranceBFS,
        meta: { needsAuth: true, permission: 'ViewInsuranceBFSReport' }
      },
      {
        path: '/reports/insuranceBSS',
        name: 'InsuranceBSS',
        component: InsuranceBSS,
        meta: { needsAuth: true, permission: 'ViewInsuranceBSSReport' }
      },
      {
        path: '/reports/deletedDX',
        name: 'DeletedDxReport',
        component: DeletedDxReport,
        meta: { needsAuth: true, permission: 'ViewDeletedDXReport' }
      },

      // SmartPN (add proper backend permissions)
      {
        path: '/smartPN/:recordID/:ID',
        name: 'SmartPN',
        props: true,
        component: () =>
          import(/* webpackChunkName: "smartPN" */ '../views/SmartPN.vue'),
        meta: { needsAuth: true, requiresAny: ['ViewPatients'] }
      },
      {
        path: '/smartPNHistory/:recordID',
        name: 'SmartPNHistory',
        props: true,
        component: () =>
          import(
            /* webpackChunkName: "smartPN" */ '../views/SmartPNHistory.vue'
          ),
        meta: { needsAuth: true, requiresAny: ['ViewPatients'] }
      }
    ]
  }
]

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes
})

router.beforeEach((to, from, next) => {
  const publicRouteNames = new Set([
    'Login',
    'SignUp',
    'PrivacyPolicy2',
    'Terms2',
    'UnderMaintenance',
    'ChangePassword',
    'OtpLoginPage'
  ])

  //console.log('TO:', to.fullPath)
  //console.log(
  //   'Matched:',
  //   to.matched.map(m => ({ path: m.path, meta: m.meta }))
  // )
  //console.log('Perms:', Array.from(getUserPermissions()))

  // 4) Post-login init
  if (
    from.name === 'Login' &&
    to.name === 'PatientsView' &&
    isAuthenticated()
  ) {
    const currentUser: CurrentUser = JSON.parse(
      localStorage.getItem('currentUser') || '{}'
    )
    const userId = String(currentUser.userId || '')
    if (userId) store.dispatch('app/initSignalR', userId)
    if (currentUser.rmtUserId) {
      store.dispatch('app/fetchNotifications', String(currentUser.rmtUserId))
    }
  }
  // 1) Authentication
  if (!publicRouteNames.has(String(to.name)) && !isAuthenticated()) {
    return next({ name: 'Login' })
  }

  // 2) Doctor selection rule
  if (to.name === 'PatientsView' && needsADoctor()) {
    return next({ name: 'DoctorSelection' })
  }

  // 3) Authorization
  // Build a flattened list of rules from all matched route records
  const isAllowAll = to.matched.some(r => (r.meta as any)?.allowAll === true)

  if (
    !publicRouteNames.has(String(to.name)) &&
    isAuthenticated() &&
    !isAllowAll
  ) {
    const perms = getUserPermissions()

    //console.log('User permissions:', perms)

    const requiredPermissions = to.matched
      .map(r => (r.meta as any)?.permission)
      .filter(Boolean) as string[]

    //console.log('Required perms:', requiredPermissions)
    const requiresAnyLists = to.matched
      .map(r => (r.meta as any)?.requiresAny)
      .filter(Boolean) as string[][]

    // 1) If ANY matched route requires a specific permission -> user must have ALL of them
    if (requiredPermissions.length > 0) {
      const ok = requiredPermissions.every(p => perms.has(p))
      if (!ok) return next({ name: 'Unauthorized' })
      return next()
    }

    // 2) Otherwise, if route declares requiresAny -> user must match at least one
    if (requiresAnyLists.length > 0) {
      const ok = requiresAnyLists.some(list => list.some(p => perms.has(p)))
      if (!ok) return next({ name: 'Unauthorized' })
      return next()
    }

    // 3) Nothing declared => block (tight security)
    return next({ name: 'Unauthorized' })
  }

  return next()
})

export default router
